import { useEffect, useState } from 'react';
import {
  ClientCsvRowSchema,
  PaymentMethodSchema,
  type ClientStatus,
  type PaymentMethod,
  type PublicUser,
  type SepaScheme,
} from '@otter/shared';
import { ClientPill } from '../components/Pill';
import { DateInput } from '../components/DateInput';
import { api, downloadFile } from '../lib/api';
import { parseCsv } from '../lib/csv';
import { fmtDate } from '../lib/format';
import { ClienteFicha, PAYMENT_METHOD_LABELS } from './ClienteFicha';

type Props = {
  user: PublicUser;
  scope: string;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (err: unknown) => void;
};

type ClientRow = {
  id: string;
  name: string;
  nif: string;
  status: ClientStatus;
  ownerId: string;
  ownerCode: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  lastActivityAt: string | null;
};

const STATUSES: Array<{ id: 'all' | ClientStatus; label: string }> = [
  { id: 'all', label: 'todos' },
  { id: 'activo', label: 'activos' },
  { id: 'mora', label: 'mora' },
  { id: 'inactivo', label: 'inactivos' },
];

export function Clientes({ user, scope, onToast, onError }: Props) {
  const [items, setItems] = useState<ClientRow[]>([]);
  const [status, setStatus] = useState<'all' | ClientStatus>('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    try {
      const q: Record<string, string> = {};
      if (status !== 'all') q.status = status;
      if (scope !== 'all') {
        const owners = await api<{ items: Array<{ id: string; code: string }> }>('/admin/team');
        const found = owners.items.find((o) => o.code === scope);
        if (found) q.ownerId = found.id;
      }
      if (search) q.search = search;
      const res = await api<{ items: ClientRow[] }>('/admin/clients', { query: q });
      setItems(res.items);
    } catch (e) {
      onError(e);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, scope, search]);

  if (openId) {
    return (
      <ClienteFicha
        id={openId}
        onClose={() => setOpenId(null)}
        onError={onError}
        onToast={onToast}
        onChanged={() => void load()}
      />
    );
  }

  return (
    <div className="body">
      <div className="pageh">
        <h1>Clientes</h1>
        <div className="sub">
          {items.length} resultado{items.length === 1 ? '' : 's'} ·{' '}
          <span className="v">scope = {scope}</span>
        </div>
        <div className="actions">
          <input
            type="file"
            accept=".csv"
            id="csv-import"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const { valid, invalid } = await parseCsv(f, ClientCsvRowSchema);
                if (invalid.length > 0) {
                  onToast(`${valid.length} válidas, ${invalid.length} inválidas (revisa el CSV)`, 'err');
                }
                if (valid.length === 0) return;
                const r = await api<{ created: number; updated: number; errors: unknown[] }>(
                  '/admin/clients/import-csv',
                  { method: 'POST', body: { rows: valid, mode: 'merge' } },
                );
                onToast(`Import: ${r.created} creados, ${r.updated} actualizados`, 'ok');
                void load();
              } catch (err) {
                onError(err);
              } finally {
                e.target.value = '';
              }
            }}
          />
          <button
            className="btn"
            onClick={() => downloadFile('/admin/clients/export-csv', 'clients.csv')}
          >
            Export CSV
          </button>
          <button
            className="btn"
            onClick={() => document.getElementById('csv-import')?.click()}
          >
            Import CSV
          </button>
          <button className="btn primary" onClick={() => setShowCreate(true)}>
            <span className="plus">+</span> Nuevo cliente
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="chips">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              className={`chip ${status === s.id ? 'on' : ''}`}
              onClick={() => setStatus(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="spacer" />
        <div className="search">
          <input
            placeholder="buscar nombre, NIF, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="kbd">/</span>
        </div>
      </div>

      <div className="card">
        <table className="t">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>NIF</th>
              <th>Comercial</th>
              <th>Estado</th>
              <th>Última act.</th>
              <th>Contacto</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} onClick={() => setOpenId(c.id)}>
                <td data-label="Cliente">
                  <b>{c.name}</b>
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>{c.city ?? '—'}</div>
                </td>
                <td className="mono" data-label="NIF">{c.nif}</td>
                <td data-label="Comercial">
                  <span className={`av-mini av-${'A'}`}>{c.ownerCode}</span>
                </td>
                <td data-label="Estado">
                  <ClientPill status={c.status} />
                </td>
                <td className="mono" data-label="Última act.">{fmtDate(c.lastActivityAt)}</td>
                <td style={{ color: 'var(--muted)' }} data-label="Contacto">{c.email ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="empty">sin clientes que mostrar</div>}
      </div>

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
            onToast('Cliente creado', 'ok');
          }}
          onError={onError}
          isAdmin={user.isSystemAdmin}
        />
      )}
    </div>
  );
}

function CreateClientModal(props: {
  onClose: () => void;
  onCreated: () => void;
  onError: (e: unknown) => void;
  isAdmin: boolean;
}) {
  const [name, setName] = useState('');
  const [nif, setNif] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [sepaMandateRef, setSepaMandateRef] = useState('');
  const [sepaMandateDate, setSepaMandateDate] = useState('');
  const [sepaScheme, setSepaScheme] = useState<SepaScheme | ''>('');
  const [busy, setBusy] = useState(false);
  const isDirectDebit = paymentMethod === 'domiciliacion';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/admin/clients', {
        method: 'POST',
        body: {
          name,
          nif,
          email: email || null,
          phone: phone || null,
          address: address || null,
          city: city || null,
          postalCode: postalCode || null,
          country: country || null,
          paymentMethod: paymentMethod || null,
          iban: iban || null,
          bic: bic || null,
          bankName: bankName || null,
          accountHolder: accountHolder || null,
          sepaMandateRef: isDirectDebit ? sepaMandateRef || null : null,
          sepaMandateDate: isDirectDebit ? sepaMandateDate || null : null,
          sepaScheme: isDirectDebit ? sepaScheme || null : null,
        },
      });
      props.onCreated();
    } catch (err) {
      props.onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Nuevo cliente</h3>
        <div className="field">
          <label>Nombre</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        <div className="field">
          <label>NIF</label>
          <input value={nif} onChange={(e) => setNif(e.target.value)} required />
        </div>
        <div className="field">
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label>Teléfono</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="field">
          <label>Dirección</label>
          <input value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        <div className="field">
          <label>Ciudad</label>
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className="field">
          <label>Código postal</label>
          <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
        </div>
        <div className="field">
          <label>País</label>
          <input value={country} onChange={(e) => setCountry(e.target.value)} />
        </div>
        <div className="field">
          <label>Método de pago</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod | '')}
          >
            <option value="">— sin especificar —</option>
            {PaymentMethodSchema.options.map((m) => (
              <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>IBAN</label>
          <input
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            placeholder="ES00 0000 0000 0000 0000 0000"
          />
        </div>
        <div className="field">
          <label>BIC / SWIFT</label>
          <input value={bic} onChange={(e) => setBic(e.target.value)} />
        </div>
        <div className="field">
          <label>Banco</label>
          <input value={bankName} onChange={(e) => setBankName(e.target.value)} />
        </div>
        <div className="field">
          <label>Titular de la cuenta</label>
          <input value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} />
        </div>
        {isDirectDebit && (
          <>
            <div className="field">
              <label>Ref. mandato SEPA</label>
              <input value={sepaMandateRef} onChange={(e) => setSepaMandateRef(e.target.value)} />
            </div>
            <div className="field">
              <label>Fecha de firma del mandato</label>
              <DateInput value={sepaMandateDate} onChange={setSepaMandateDate} />
            </div>
            <div className="field">
              <label>Esquema SEPA</label>
              <select
                value={sepaScheme}
                onChange={(e) => setSepaScheme(e.target.value as SepaScheme | '')}
              >
                <option value="">— sin especificar —</option>
                <option value="CORE">CORE (general)</option>
                <option value="B2B">B2B (entre empresas)</option>
              </select>
            </div>
          </>
        )}
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            Crear
          </button>
        </div>
      </form>
    </div>
  );
}
